"use client";

import { useState, useEffect, useCallback } from "react";
import { usePublicAuth } from "@/lib/auth/PublicAuthContext";
import {
  bookmarksApi,
  Bookmark,
  BOOKMARK_CATEGORIES,
  BookmarkCategory,
} from "@/lib/api/public-client";
import HorizontalBigImageCategoryNews from "../../../components/component_horizontal/HorizontalBigImageCategoryNews";
import { VerticalSmallImagesNews } from "../../../components/component_vertical/VerticalSmallImagesNews";
import { VerticalMediumVideoNews } from "../../../components/component_vertical/VerticalMediumVideoNews";
import { FALLBACK_IMAGE } from '@/lib/constants';
import { getTimeAgo } from '@/lib/componentutills';

interface Tab {
  id: string;
  label: string;
  category: BookmarkCategory;
}

const tabs: Tab[] = [
  { id: "news", label: "செய்தி", category: BOOKMARK_CATEGORIES.NEWS },
  { id: "photos", label: "புகைப்படங்கள்", category: BOOKMARK_CATEGORIES.PHOTOS },
  { id: "videos", label: "வீடியோக்கள்", category: BOOKMARK_CATEGORIES.VIDEOS },
];

export default function BookmarkTabs() {
  const { isAuthenticated } = usePublicAuth();
  const [activeTab, setActiveTab] = useState<string>("news");
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  const [categoryCounts, setCategoryCounts] = useState<
    { category: string; count: number }[]
  >([]);
  const [loading, setLoading] = useState(false);

  const activeTabData = tabs.find((t) => t.id === activeTab);

  const loadCategoryCounts = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const { categories } = await bookmarksApi.getCategories();
      setCategoryCounts(categories);
    } catch (error) {
      console.error("Failed to load category counts:", error);
    }
  }, [isAuthenticated]);

  const loadBookmarks = useCallback(async () => {
    if (!isAuthenticated || !activeTabData) return;

    setLoading(true);
    try {
      const { bookmarks: data } = await bookmarksApi.getBookmarks({
        category: activeTabData.category,
        limit: 50,
      });
      setBookmarks(data);
    } catch (error) {
      console.error("Failed to load bookmarks:", error);
      setBookmarks([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, activeTabData]);

  useEffect(() => {
    loadCategoryCounts();
  }, [loadCategoryCounts]);

  useEffect(() => {
    loadBookmarks();
  }, [loadBookmarks]);

  const handleRemoveBookmark = async (slug: string) => {
    try {
      await bookmarksApi.removeBookmark(slug);
      setBookmarks((prev) => prev.filter((b) => b.slug !== slug));
      loadCategoryCounts();
    } catch (error) {
      console.error("Failed to remove bookmark:", error);
    }
  };

  const getCategoryCount = (category: string): number => {
    const found = categoryCounts.find((c) => c.category === category);
    return found ? found.count : 0;
  };

  // Use getTimeAgo from componentutills - convert date string to timestamp
  const formatTimeAgo = (dateString: string): string => getTimeAgo(new Date(dateString).getTime());

  const renderEmptyState = () => {
    const messages: Record<string, string> = {
      news: "நீங்கள் இன்னும் எந்த செய்தியையும் புக்மார்க் செய்யவில்லை",
      photos: "நீங்கள் இன்னும் எந்த புகைப்படத்தையும் புக்மார்க் செய்யவில்லை",
      videos: "நீங்கள் இன்னும் எந்த வீடியோவையும் புக்மார்க் செய்யவில்லை",
    };

    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="w-16 h-16 mb-4 text-DCDCDC">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="w-full h-full"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0Z"
            />
          </svg>
        </div>
        <p className="text-16-tamil-500 text-4D4D4D text-center">
          {messages[activeTab]}
        </p>
        <p className="text-14-tamil-400 text-808080 text-center mt-2">
          செய்திகளை புக்மார்க் செய்ய, புக்மார்க் ஐகானைக் கிளிக் செய்யவும்
        </p>
      </div>
    );
  };

  const renderLoginRequired = () => {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="w-16 h-16 mb-4 text-DCDCDC">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="w-full h-full"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"
            />
          </svg>
        </div>
        <p className="text-16-tamil-500 text-4D4D4D text-center">
          புக்மார்க்களைக் காண உள்நுழையவும்
        </p>
        <p className="text-14-tamil-400 text-808080 text-center mt-2">
          உங்கள் சேமித்த செய்திகளை அணுக உள்நுழைக்கவும்
        </p>
      </div>
    );
  };

  const renderBookmarkItem = (bookmark: Bookmark) => {
    // Use FieldConfig format for component props
    const commonProps = {
      title: { value: bookmark.title || "Untitled" },
      imageSrc: { value: bookmark.thumbnail_url || FALLBACK_IMAGE },
      slug: bookmark.slug,
      timeAgo: { value: formatTimeAgo(bookmark.created_at) },
    };

    // News uses HorizontalBigImageCategoryNews
    if (activeTab === "news") {
      return (
        <div key={bookmark.id} className="relative group">
          <HorizontalBigImageCategoryNews
            category={{ value: "செய்தி" }}
            title={commonProps.title}
            excerpt={{ value: bookmark.description || "" }}
            timeAgo={commonProps.timeAgo}
            imageSrc={commonProps.imageSrc}
            slug={commonProps.slug}
          />
          <button
            onClick={() => handleRemoveBookmark(bookmark.slug)}
            className="absolute top-2 right-2 p-2 bg-white/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-100"
            aria-label="Remove bookmark"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#EF4444"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      );
    }

    // Photos use VerticalSmallImagesNews
    if (activeTab === "photos") {
      return (
        <div key={bookmark.id} className="relative group">
          <VerticalSmallImagesNews
            title={commonProps.title}
            timeAgo={commonProps.timeAgo}
            imageSrc={commonProps.imageSrc}
            slug={commonProps.slug}
          />
          <button
            onClick={() => handleRemoveBookmark(bookmark.slug)}
            className="absolute top-2 right-2 p-2 bg-white/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-100"
            aria-label="Remove bookmark"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#EF4444"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      );
    }

    // Videos use VerticalMediumVideoNews
    if (activeTab === "videos") {
      return (
        <div key={bookmark.id} className="relative group">
          <VerticalMediumVideoNews
            title={commonProps.title}
            timeAgo={commonProps.timeAgo}
            imageSrc={commonProps.imageSrc}
            slug={commonProps.slug}
          />
          <button
            onClick={() => handleRemoveBookmark(bookmark.slug)}
            className="absolute top-2 right-2 p-2 bg-white/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-100"
            aria-label="Remove bookmark"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#EF4444"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      );
    }

    return null;
  };

  const renderContent = () => {
    if (!isAuthenticated) {
      return renderLoginRequired();
    }

    if (loading) {
      return (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-012061"></div>
        </div>
      );
    }

    if (bookmarks.length === 0) {
      return renderEmptyState();
    }

    // 2-column grid layout for all tabs
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
        {bookmarks.map(renderBookmarkItem)}
      </div>
    );
  };

  return (
    <div className="w-full">
      {/* Tab Header */}
      <div className="flex border-b border-DCDCDC bg-F8F8F8">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-3 px-4 text-center text-14-tamil-500 transition-colors relative ${activeTab === tab.id
                ? "text-012061 text-14-tamil-700"
                : "text-4D4D4D hover:text-012061"
              }`}
          >
            <span>{tab.label}</span>
            {isAuthenticated && getCategoryCount(tab.category) > 0 && (
              <span className="ml-1 text-12-english-400 text-808080">
                ({getCategoryCount(tab.category)})
              </span>
            )}
            {activeTab === tab.id && (
              <span className="absolute bottom-0 left-0 right-0 h-[3px] bg-012061" />
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-FFFFFF min-h-[400px]">{renderContent()}</div>
    </div>
  );
}
